import { defineStore } from 'pinia'
import { type Ref, ref } from 'vue'
import type UtilisateurList from '@/interfaces/Utilisateur.ts'
import useFetch from '@/composables/useFetch.ts'
import type RoleList from '@/interfaces/Role.ts'
import type EquipementList from '@/interfaces/Equipement.ts'
import type {
  ProduitFiltrationCategorie,
  ProduitFiltrationList,
} from '@/interfaces/ProduitFiltrationList.ts'
import type FabricantList from '@/interfaces/Fabricant.ts'
import type ClientList from '@/interfaces/Client.ts'
import type { SessionList } from '@/interfaces/Session.ts'
import type { CleAccesList } from '@/interfaces/CleAcces.ts'
import type { OrdreFabricationGet } from '@/interfaces/OrdreFabrication.ts'

export const useCacheStore = defineStore('cache', () => {
  // dépendances
  const api = useFetch()

  // données
  const online: Ref<boolean> = ref(navigator.onLine)

  // données en cache
  const cles: Ref<CleAccesList[]> = ref([])
  const clients: Ref<ClientList[]> = ref([])
  const equipements: Ref<EquipementList[]> = ref([])
  const equipementsCategories: Ref<EquipementList[]> = ref([])
  const fabricants: Ref<FabricantList[]> = ref([])
  const ordresFabrication: Ref<OrdreFabricationGet[]> = ref([])
  const produits: Ref<ProduitFiltrationList[]> = ref([])
  const produitsCategories: Ref<ProduitFiltrationCategorie[]> = ref([])
  const roles: Ref<RoleList[]> = ref([])
  const sessions: Ref<SessionList[]> = ref([])
  const utilisateurs: Ref<UtilisateurList[]> = ref([])

  // fonctions
  async function getCles() {
    if (online.value) return await api.get('cle-acces/')
    else return cles.value
  }
  async function getClients() {
    if (online.value) return await api.get('clients/')
    else return clients.value
  }
  async function getClient(clientId: string) {
    if (online.value) return await api.get(`clients/${clientId}`)
    else return clients.value.find((client) => client.id === clientId)
  }
  async function getEquipements() {
    if (online.value) return await api.get('equipements/')
    else return equipements.value
  }
  async function getEquipementsCategories() {
    if (online.value) return await api.get('equipements/categories')
    else return equipementsCategories.value
  }
  async function getEquipement(equipementId: string) {
    if (online.value) return await api.get(`utilisateurs/${equipementId}`)
    else return equipements.value.find((equipement) => equipement.id === equipementId)
  }
  async function getFabricants() {
    if (online.value) return await api.get('fabricants/')
    else return fabricants.value
  }
  async function getOrdresFabrication() {
    if (online.value) return await api.get('ordres-fabrication/')
    else return ordresFabrication.value
  }
  async function getOrdreFabrication(ordreFabricationId: string) {
    if (online.value) return await api.get(`ordres-fabrication/${ordreFabricationId}`)
    else
      return ordresFabrication.value.find(
        (ordreFabrication) => ordreFabrication.id === ordreFabricationId,
      )
  }
  async function getFabricant(fabricantId: string) {
    if (online.value) return await api.get(`fabricants/${fabricantId}`)
    else return fabricants.value.find((fabricant) => fabricant.id === fabricantId)
  }
  async function getProduits() {
    if (online.value) return await api.get('produits-filtration/')
    else return produits.value
  }
  async function getProduitsCategories() {
    if (online.value) return await api.get('produits-filtration/categories/')
    else return produitsCategories.value
  }
  async function getProduit(produitId: string) {
    if (online.value) return await api.get(`produits-filtration/${produitId}`)
    else return produits.value.find((produit) => produit.id === produitId)
  }
  async function getRoles() {
    if (online.value) return await api.get('roles/')
    else return roles.value
  }
  async function getSessions() {
    if (online.value) return await api.get('sessions/')
    else return sessions.value
  }
  async function getUtilisateurs() {
    if (online.value) return await api.get('utilisateurs/')
    else return utilisateurs.value
  }

  async function getUtilisateur(utilisateurId: string) {
    if (online.value) return await api.get(`utilisateurs/${utilisateurId}`)
    else return utilisateurs.value.find((utilisateur) => utilisateur.id === utilisateurId)
  }

  async function getCache() {
    cles.value = await getCles()
    clients.value = await getClients()
    equipements.value = await getEquipements()
    equipementsCategories.value = await getEquipementsCategories()
    fabricants.value = await getFabricants()
    ordresFabrication.value = await getOrdresFabrication()
    produits.value = await getProduits()
    produitsCategories.value = await getProduitsCategories()
    roles.value = await getRoles()
    sessions.value = await getSessions()
    utilisateurs.value = await getUtilisateurs()
  }

  // évènements
  addEventListener('online', () => {
    online.value = true
  })
  addEventListener('offline', () => {
    online.value = false
  })

  return {
    getCache,
    getCles,
    getClients,
    getClient,
    getEquipements,
    getEquipementsCategories,
    getEquipement,
    getFabricants,
    getFabricant,
    getOrdresFabrication,
    getOrdreFabrication,
    getProduits,
    getProduitsCategories,
    getProduit,
    getRoles,
    getSessions,
    getUtilisateurs,
    getUtilisateur,
    online,
  }
})
